import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, X, Trash2, Edit3, RefreshCw } from 'lucide-react';
import { Button } from '../common/Button';
import { tenantApi } from '../../services/api';
import { CleanupTableConfig } from '../../types/WmsTenant';

type EditableConfig = Omit<CleanupTableConfig, 'id' | 'createdDate' | 'modifiedDate'>;

const emptyConfig = (nextOrder: number): EditableConfig => ({
  tableName: '',
  deleteOrder: nextOrder,
  isActive: true,
  category: '',
  preAction: '',
  requiresDisabledTriggers: false,
  description: ''
});

export const CleanupTablesSection: React.FC = () => {
  const [tables, setTables] = useState<CleanupTableConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditableConfig | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newForm, setNewForm] = useState<EditableConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tenantApi.getCleanupTables();
      setTables(data.sort((a, b) => a.deleteOrder - b.deleteOrder || a.tableName.localeCompare(b.tableName)));
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke hente cleanup-tabeller.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (row: CleanupTableConfig) => {
    setEditingId(row.id);
    setEditForm({
      tableName: row.tableName,
      deleteOrder: row.deleteOrder,
      isActive: row.isActive,
      category: row.category || '',
      preAction: row.preAction || '',
      requiresDisabledTriggers: row.requiresDisabledTriggers,
      description: row.description || ''
    });
    setCreatingNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
    if (!editForm || editingId === null) return;
    try {
      setSaving(true);
      await tenantApi.updateCleanupTable(editingId, editForm);
      setEditingId(null);
      setEditForm(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke lagre.');
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    const nextOrder = tables.length > 0
      ? Math.max(...tables.map(t => t.deleteOrder)) + 10
      : 10;
    setNewForm(emptyConfig(nextOrder));
    setCreatingNew(true);
    setEditingId(null);
  };

  const cancelCreate = () => {
    setCreatingNew(false);
    setNewForm(null);
  };

  const saveCreate = async () => {
    if (!newForm) return;
    if (!newForm.tableName.trim()) {
      setError('Tabellnavn er påkrevd.');
      return;
    }
    try {
      setSaving(true);
      await tenantApi.createCleanupTable(newForm);
      setCreatingNew(false);
      setNewForm(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke opprette.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CleanupTableConfig) => {
    if (!window.confirm(`Slett cleanup-konfigurasjonen for "${row.tableName}"?`)) return;
    try {
      await tenantApi.deleteCleanupTable(row.id);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke slette.');
    }
  };

  const toggleActive = async (row: CleanupTableConfig) => {
    try {
      await tenantApi.updateCleanupTable(row.id, {
        tableName: row.tableName,
        deleteOrder: row.deleteOrder,
        isActive: !row.isActive,
        category: row.category,
        preAction: row.preAction,
        requiresDisabledTriggers: row.requiresDisabledTriggers,
        description: row.description
      });
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke oppdatere status.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
        <span className="text-gray-500">Laster cleanup-tabeller...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Konfigurasjon av tabeller som tømmes ved cleanup. Lavere DeleteOrder kjøres først.
        </p>
        <div className="flex space-x-2">
          <Button variant="secondary" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Oppdater
          </Button>
          <Button variant="primary" onClick={startCreate} disabled={creatingNew}>
            <Plus className="w-4 h-4 mr-2" />
            Ny tabell
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-start">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Order</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tabellnavn</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">PreAction</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Disable triggere</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Aktiv</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">Handlinger</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {creatingNew && newForm && (
              <EditRow
                form={newForm}
                setForm={setNewForm as React.Dispatch<React.SetStateAction<EditableConfig | null>>}
                onSave={saveCreate}
                onCancel={cancelCreate}
                saving={saving}
                isNew
              />
            )}

            {tables.map(row => (
              editingId === row.id && editForm ? (
                <EditRow
                  key={row.id}
                  form={editForm}
                  setForm={setEditForm}
                  onSave={saveEdit}
                  onCancel={cancelEdit}
                  saving={saving}
                />
              ) : (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm text-gray-900">{row.deleteOrder}</td>
                  <td className="px-3 py-2 text-sm font-mono text-gray-900">{row.tableName}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{row.category || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{row.description || '-'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-500 max-w-xs truncate" title={row.preAction || ''}>
                    {row.preAction || '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.requiresDisabledTriggers ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-500" title="Triggere deaktiveres"></span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleActive(row)}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        row.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Klikk for å bytte status"
                    >
                      {row.isActive ? 'Aktiv' : 'Inaktiv'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      onClick={() => startEdit(row)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Rediger"
                      disabled={editingId !== null || creatingNew}
                    >
                      <Edit3 className="w-4 h-4 inline" />
                    </button>
                    <button
                      onClick={() => handleDelete(row)}
                      className="text-red-500 hover:text-red-700"
                      title="Slett"
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              )
            ))}

            {tables.length === 0 && !creatingNew && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  Ingen cleanup-tabeller konfigurert. Klikk "Ny tabell" for å legge til.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EditRow: React.FC<{
  form: EditableConfig;
  setForm: React.Dispatch<React.SetStateAction<EditableConfig | null>>;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew?: boolean;
}> = ({ form, setForm, onSave, onCancel, saving, isNew }) => {
  const update = <K extends keyof EditableConfig>(key: K, value: EditableConfig[K]) => {
    setForm(prev => prev ? { ...prev, [key]: value } : prev);
  };

  return (
    <tr className={isNew ? 'bg-blue-50' : 'bg-yellow-50'}>
      <td className="px-3 py-2">
        <input
          type="number"
          value={form.deleteOrder}
          onChange={e => update('deleteOrder', parseInt(e.target.value) || 0)}
          className="w-16 border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={form.tableName}
          onChange={e => update('tableName', e.target.value)}
          placeholder="Tabellnavn"
          className="w-full border rounded px-2 py-1 text-sm font-mono"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={form.category || ''}
          onChange={e => update('category', e.target.value)}
          placeholder="Kategori"
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={form.description || ''}
          onChange={e => update('description', e.target.value)}
          placeholder="Beskrivelse"
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={form.preAction || ''}
          onChange={e => update('preAction', e.target.value)}
          placeholder="Valgfri SQL som kjøres før TRUNCATE"
          className="w-full border rounded px-2 py-1 text-xs font-mono"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={form.requiresDisabledTriggers}
          onChange={e => update('requiresDisabledTriggers', e.target.checked)}
          className="rounded"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={e => update('isActive', e.target.checked)}
          className="rounded"
        />
      </td>
      <td className="px-3 py-2 text-right space-x-2">
        <button
          onClick={onSave}
          disabled={saving || !form.tableName.trim()}
          className="text-green-600 hover:text-green-800 disabled:text-gray-300"
          title="Lagre"
        >
          <Save className="w-4 h-4 inline" />
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-gray-500 hover:text-gray-700"
          title="Avbryt"
        >
          <X className="w-4 h-4 inline" />
        </button>
      </td>
    </tr>
  );
};

export default CleanupTablesSection;
